import { useMemo } from 'react';

import type { MovementStage, MovementStatusResponse, NotificationResponse } from '../../api';
import {
  AsyncSection,
  DataTable,
  PageHeader,
  Panel,
  useNotifications,
  type Column,
} from '../../components';
import { formatDate, formatDateTime, formatInteger } from '../../format';
import {
  useMarkAllNotificationsRead,
  useMarkNotificationRead,
  useMovementStatus,
  useNotifications as useNoticeFeed,
  usePortalContracts,
} from '../queries';
import { useCompanyScope } from './companyScope';
import {
  ACTION_LABELS,
  EVENT_LABELS,
  PORTAL_COPY,
  STAGE_DESCRIPTIONS,
  STAGE_LABELS,
  STAGE_ORDER,
  awaitingCustomer,
  movementPerson,
  noticeSubject,
} from './portal';

/**
 * Where every request stands, and what we have told this company about them.
 *
 * This screen is the reassurance the email thread currently provides. The
 * question it exists to answer without anybody writing to ask is "did my change
 * make it into this month's file?", so the thing a row leads with is the stage
 * and the month, not the raw lifecycle status the API records.
 *
 * **The stage comes from the server and is never recomputed here.** It is
 * derived from three layers at once -- what was decided, which cutoff the
 * movement lands on, and whether that cutoff has been signed off -- and a screen
 * that worked any part of that out for itself would eventually tell a customer
 * something the back-office disagrees with. The same goes for the month: it
 * arrives beside the stage, on the same row, so a line reading "agendada" cannot
 * be paired with a month fetched separately.
 *
 * The three panels are in the order somebody reads them in: what is waiting on
 * *you*, then the news, then everything. A person who has been emailed about a
 * returned movement opens this page to find out what to do, and the thing to do
 * is at the top.
 */

/** One stage and its count, as the summary renders it. */
interface StageTally {
  readonly stage: MovementStage;
  readonly count: number;
}

function summaryOf(counts: Record<string, number>): readonly StageTally[] {
  return STAGE_ORDER.map((stage) => ({ stage, count: counts[stage] ?? 0 }));
}

/**
 * The stage, with its explanation attached.
 *
 * The sentence is a `title` rather than a second visible line because the table
 * has eight rows' worth of them and repeating the paragraph per row would bury
 * the movements themselves. The summary above the table carries the same
 * sentences where there is room to read them.
 */
function stageCell(item: MovementStatusResponse) {
  return (
    <span className="stage" title={STAGE_DESCRIPTIONS[item.stage]}>
      {STAGE_LABELS[item.stage]}
    </span>
  );
}

const MOVEMENT_COLUMNS: readonly Column<MovementStatusResponse>[] = [
  { key: 'stage', header: 'Situação', render: stageCell },
  {
    key: 'action',
    header: 'Movimentação',
    render: (item) => ACTION_LABELS[item.delta.action],
  },
  { key: 'person', header: 'Pessoa', render: movementPerson },
  {
    key: 'effective_date',
    header: 'Vigência',
    align: 'end',
    render: (item) => formatDate(item.delta.effective_date),
  },
  {
    key: 'cycle_month',
    header: PORTAL_COPY.cycleColumn,
    render: (item) => item.cycle_month,
    headerHint: 'O fechamento mensal em que esta movimentação entra.',
  },
  {
    key: 'cutoff_date',
    header: PORTAL_COPY.cutoffColumn,
    align: 'end',
    render: (item) => formatDate(item.cutoff_date),
    headerHint: 'A data em que esse fechamento lê o cadastro.',
  },
  {
    key: 'reason',
    header: 'Motivo',
    render: (item) =>
      item.delta.decision_reason !== null && item.delta.decision_reason !== undefined ? (
        item.delta.decision_reason
      ) : (
        <span className="table__muted">—</span>
      ),
  },
  {
    key: 'submitted_at',
    header: 'Pedido em',
    align: 'end',
    render: (item) => formatDateTime(item.delta.submitted_at),
  },
];

export function MovementStatusPage() {
  const { company } = useCompanyScope();
  const { notify } = useNotifications();

  const contracts = usePortalContracts(company.id);
  const contract = contracts.data?.items[0];

  // No cycle month is passed: which cutoff is being worked on is the server's
  // answer, in São Paulo, and a browser clock set to the wrong month would
  // otherwise change what "agendada" means on this screen.
  const status = useMovementStatus(company.id, contract?.id);
  const notices = useNoticeFeed(company.id);

  const markRead = useMarkNotificationRead();
  const markAllRead = useMarkAllNotificationsRead();

  const movements = useMemo(() => status.data?.items ?? [], [status.data]);
  const needsYou = useMemo(() => awaitingCustomer(movements), [movements]);

  const noticeColumns: readonly Column<NotificationResponse>[] = [
    {
      key: 'event',
      header: 'Aviso',
      render: (notice) => (
        <span className={notice.read_at === null ? 'news news--unread' : 'news'}>
          {EVENT_LABELS[notice.event]}
        </span>
      ),
    },
    {
      key: 'subject',
      header: 'Sobre',
      render: (notice) => {
        const subject = noticeSubject(notice, movements);
        return subject ?? <span className="table__muted">—</span>;
      },
    },
    {
      key: 'occurred_at',
      header: 'Quando',
      align: 'end',
      render: (notice) => formatDateTime(notice.occurred_at),
    },
    {
      key: 'actions',
      header: 'Ações',
      render: (notice) =>
        notice.read_at === null ? (
          <button
            type="button"
            className="button button--ghost"
            disabled={markRead.isPending}
            onClick={() =>
              markRead.mutate(
                { companyId: company.id, notificationId: notice.id },
                { onError: () => notify('error', PORTAL_COPY.markReadFailed) },
              )
            }
          >
            {PORTAL_COPY.markRead}
          </button>
        ) : (
          <span className="table__muted">—</span>
        ),
    },
  ];

  return (
    <div className="page">
      <PageHeader title={PORTAL_COPY.statusTitle} description={PORTAL_COPY.statusDescription} />

      <AsyncSection
        state={contracts}
        loadingLabel="Carregando contrato…"
        errorTitle="Não foi possível carregar o contrato"
        isEmpty={(data) => data.items.length === 0}
        emptyTitle="Nenhum contrato"
        emptyDescription={PORTAL_COPY.noContract}
      >
        {() => (
          <>
            {needsYou.length > 0 && (
              <Panel
                title={PORTAL_COPY.needsYouTitle}
                description={PORTAL_COPY.needsYouDescription}
              >
                <DataTable
                  id="customer-returned-movements"
                  caption="Movimentações devolvidas para correção"
                  columns={MOVEMENT_COLUMNS}
                  rows={needsYou}
                  rowKey={(item) => item.delta.id}
                />
              </Panel>
            )}

            <Panel title={PORTAL_COPY.noticesTitle} description={PORTAL_COPY.noticesDescription}>
              <AsyncSection
                state={notices}
                loadingLabel="Carregando avisos…"
                errorTitle="Não foi possível carregar os avisos"
              >
                {(data) => (
                  <>
                    {data.unread_count > 0 && (
                      <p className="panel__actions">
                        <span className="badge badge--unread">
                          {formatInteger(data.unread_count)} {PORTAL_COPY.unreadBadge}
                        </span>
                        <button
                          type="button"
                          className="button button--ghost"
                          disabled={markAllRead.isPending}
                          onClick={() =>
                            markAllRead.mutate(
                              { companyId: company.id },
                              { onError: () => notify('error', PORTAL_COPY.markReadFailed) },
                            )
                          }
                        >
                          {markAllRead.isPending
                            ? PORTAL_COPY.markAllReadPending
                            : PORTAL_COPY.markAllRead}
                        </button>
                      </p>
                    )}
                    <DataTable
                      id="customer-movement-notices"
                      caption="Avisos sobre as movimentações desta empresa"
                      columns={noticeColumns}
                      rows={data.items}
                      rowKey={(notice) => notice.id}
                      emptyTitle={PORTAL_COPY.noNotices}
                      emptyDescription={PORTAL_COPY.noNoticesHint}
                    />
                  </>
                )}
              </AsyncSection>
            </Panel>

            <Panel
              title={PORTAL_COPY.statusPanelTitle}
              description={PORTAL_COPY.statusPanelDescription}
            >
              <AsyncSection
                state={status}
                loadingLabel="Carregando movimentações…"
                errorTitle="Não foi possível carregar as movimentações"
                isEmpty={(data) => data.items.length === 0}
                emptyTitle={PORTAL_COPY.noMovements}
                emptyDescription={PORTAL_COPY.noMovementsHint}
              >
                {(data) => (
                  <>
                    <dl className="summary">
                      {summaryOf(data.counts).map(({ stage, count }) => (
                        <div className="summary__item" key={stage}>
                          <dt title={STAGE_DESCRIPTIONS[stage]}>{STAGE_LABELS[stage]}</dt>
                          <dd>{formatInteger(count)}</dd>
                        </div>
                      ))}
                    </dl>

                    <DataTable
                      id="customer-movements"
                      caption="Todas as movimentações deste contrato e onde cada uma está"
                      columns={MOVEMENT_COLUMNS}
                      rows={data.items}
                      rowKey={(item) => item.delta.id}
                      emptyTitle={PORTAL_COPY.noMovements}
                    />

                    <p className="panel__footnote">
                      {formatInteger(data.total)} movimentação(ões) no total, relativas ao
                      fechamento de {data.cycle_month}.
                    </p>
                  </>
                )}
              </AsyncSection>
            </Panel>
          </>
        )}
      </AsyncSection>
    </div>
  );
}
